/**
 * TemplateChipRow — ONE component for all five D14 template groups.
 *
 *   [+ chip] [+ chip] [+ chip] [+ chip]  [See all 12]  [Save…]
 *
 * D18 (declutter, variant A): the group no longer owns a row. It is rendered
 * INSIDE the section's own header line (`variant="inline"`, the default) —
 * Symptoms header, ℞ Medicines header, the Tests line, the Diagnosis line — so
 * the pad loses the four tinted bands and their duplicate labels. `variant="row"`
 * keeps the standalone, labelled shape for the two places that have no header to
 * fold into: the pad's top whole-prescription group, and the lab-tests group
 * when the lab module is off and the Tests strip is not on screen.
 *
 * ONE neutral chip tone for every category (a deliberate reversal of the earlier
 * five-hue treatment): the row's POSITION says which section it serves, and the
 * five hues fought the pad's own blue/amber/teal pills. Category colour survives
 * inside the All-templates list, where categories are genuinely mixed.
 *
 * A category with nothing saved renders NOTHING (D14/S10 AC5). `alwaysShow` is
 * used by exactly one caller — the top group — which at zero templates shows the
 * first-use line and the `Save…` button instead of chips.
 *
 * Chip click = ADD (merge). There is no confirm on a chip, ever — Replace lives
 * only inside the All-templates list.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  TEMPLATE_ROW_LABELS,
  TEMPLATE_ROW_COPY,
} from '../../utils/rxTemplates';

// Below this host width a header line cannot hold chips: the group collapses to
// the opener + Save… and the chips are reached through the (portalled) list.
export const NARROW_HOST_PX = 380;

/** The opener's filled/pressed surface — one value, both components. */
export const OPENER_TEAL = '#0d9488';

/** A list glyph and a chevron — drawn, so no new string and no new asset. */
export function ListGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false"
      style={{ display: 'block', flexShrink: 0 }}>
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M2 4h12M2 8h12M2 12h8" />
      </g>
    </svg>
  );
}

export function Chevron({ open = false }) {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true" focusable="false"
      data-open={open ? 'true' : 'false'}
      style={{
        display: 'block', flexShrink: 0,
        transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
      }}>
      <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function TemplateChipRow({
  category,
  selection,
  onAdd,
  onOpenAll,
  onSave,              // opens the shared save form with this category preselected
  saveAffordance,      // { disabled, title } — derived from the pad, single source
  isDark = false,
  alwaysShow = false,
  variant = 'inline',
  // `variant="strip"` (D20) is hosted by TemplateStrip, which measures the host
  // once for the whole strip and tells the body what it found — so the body
  // does not measure a second time and the two can never disagree.
  narrow: narrowProp = null,
  // D30 (owner, live run) — the category the All-templates list is CURRENTLY
  // open for, so this row's opener can show it is the one that opened it.
  openCategory = null,
}) {
  const count = selection?.counts?.[category] ?? 0;
  const chips = selection?.top?.[category] || [];
  const total = selection?.total ?? 0;

  // Narrow host (the Visit Actions accordion). Measured, not guessed; a width of
  // 0 means "not laid out" (jsdom, first paint) and is never treated as narrow.
  const hostRef = useRef(null);
  const [measuredNarrow, setMeasuredNarrow] = useState(false);
  const narrow = narrowProp === null ? measuredNarrow : narrowProp;
  useLayoutEffect(() => {
    if (narrowProp !== null) return undefined;   // the host owns the measure
    const measure = () => {
      const w = hostRef.current?.getBoundingClientRect().width || 0;
      setMeasuredNarrow((prev) => {
        const next = w > 0 && w < NARROW_HOST_PX;
        return prev === next ? prev : next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [narrowProp]);

  if (count === 0 && !alwaysShow) return null;

  const ink = isDark ? '#e2e8f0' : '#334155';
  const inkFaint = isDark ? '#94a3b8' : '#64748b';
  const chipBg = isDark ? '#1e293b' : '#f8fafc';
  const chipBorder = isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1';
  const teal = isDark ? '#5eead4' : '#0d9488';

  const base = {
    padding: '0.25rem 0.6rem', borderRadius: '2rem',
    border: `1px solid ${chipBorder}`, background: chipBg, color: ink,
    fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif', whiteSpace: 'nowrap', flexShrink: 0,
  };
  const chipStyle = { ...base, maxWidth: '11rem', overflow: 'hidden', textOverflow: 'ellipsis' };

  const showChips = !narrow && count > 0;
  // FE-24 (owner live run) — `+ New template`, Edit and Delete live ONLY inside
  // the All-templates list, and this opener is the only door to it. So it
  // renders whenever the doctor has ANY template, even when this category's
  // own chips already show everything it holds (n ≤ 4) or holds none at all —
  // `See all 0` on a tab is acceptable (D20), a category with no way into the
  // list is not. Zero templates ANYWHERE keeps today's first-use line +
  // `Save…`, which is the create path at that point.
  const showOpener = count > 0 || total > 0;

  return (
    <div
      ref={hostRef}
      data-testid={`template-chip-row-${category}`}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.3rem',
        // Never a sideways scroller (owner ruling): the group wraps. On the
        // Symptoms header — the one line that also carries selected symptom
        // chips — that wrap IS the graceful degradation to a second line.
        flexWrap: 'wrap',
        // Folded onto a section header: sit at the END of that line. Inside the
        // strip (D20) the block owns its own line, so neither applies.
        marginLeft: variant === 'inline' ? 'auto' : 0,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {variant === 'row' && (
        <span style={{
          fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', color: inkFaint,
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>{TEMPLATE_ROW_LABELS[category]}</span>
      )}

      {/* First use: the one place the feature can announce itself, since every
          per-section group renders nothing at zero (D14). Inside the strip the
          line belongs to the strip's own first line, not to this body. */}
      {total === 0 && alwaysShow && variant !== 'strip' && (
        <span style={{ fontSize: '0.75rem', color: inkFaint, maxWidth: '32rem' }}>
          {TEMPLATE_ROW_COPY.zeroLine}
        </span>
      )}

      {showChips && chips.map((t) => (
        <button
          key={t.template_id}
          type="button"
          onClick={() => onAdd(t)}
          aria-label={TEMPLATE_ROW_COPY.chipAriaLabel(t)}
          title={TEMPLATE_ROW_COPY.chipAriaLabel(t)}
          style={chipStyle}
        >
          <span style={{ color: teal, fontWeight: 800, marginRight: '0.15rem' }}>+</span>
          {t.name}
        </button>
      ))}

      {showOpener && (
        // Owner, live run: "`See all n` reads like the `+ Fever meds` chips and
        // gives no sign the list is open from it." It is a different KIND of
        // control — it opens a panel, it does not add anything — so it is an
        // outlined BUTTON with a list glyph and a chevron, never the chips'
        // rounded `+` pill, and it holds a pressed/open state while the list is
        // open from it.
        <button
          type="button"
          data-testid={`template-opener-${category}`}
          onClick={(e) => onOpenAll(category, e.currentTarget)}
          aria-label={TEMPLATE_ROW_COPY.openerAriaLabel(count, category)}
          aria-expanded={openCategory === category}
          style={{
            ...base,
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            borderRadius: '0.375rem',
            background: openCategory === category ? OPENER_TEAL : (isDark ? '#0f172a' : '#ffffff'),
            color: openCategory === category ? '#ffffff' : teal,
            borderColor: openCategory === category ? OPENER_TEAL : (isDark ? 'rgba(255,255,255,0.28)' : '#94a3b8'),
          }}
        >
          <ListGlyph />
          {TEMPLATE_ROW_COPY.openerLabel(count)}
          <Chevron open={openCategory === category} />
        </button>
      )}

      {onSave && (
        <button
          type="button"
          onClick={() => onSave(category)}
          disabled={!!saveAffordance?.disabled}
          title={saveAffordance?.title}
          style={{
            ...base,
            background: isDark ? '#0f172a' : '#ffffff',
            color: saveAffordance?.disabled ? inkFaint : teal,
            borderColor: saveAffordance?.disabled ? chipBorder : teal,
            cursor: saveAffordance?.disabled ? 'default' : 'pointer',
            opacity: saveAffordance?.disabled ? 0.7 : 1,
          }}
        >{TEMPLATE_ROW_COPY.saveLabel}</button>
      )}
    </div>
  );
}
